import * as THREE from "../../libs/three.js/build/three.module.js";
import { PointCloudOctreeGeometry, PointCloudOctreeGeometryNode } from "../PointCloudOctreeGeometry.js";
import { Version } from "../Version.js";
import { XHRFactory } from "../XHRFactory.js";
import { LasLazLoader } from "./LasLazLoader.js";
import { PointAttribute, PointAttributes, PointAttributeTypes } from "./PointAttributes.js";

function lasLazAttributes() {
    const attributes = new PointAttributes();

    attributes.add(PointAttribute.POSITION_CARTESIAN);
    attributes.add(new PointAttribute("rgba", PointAttributeTypes.DATA_TYPE_UINT8, 4));
    attributes.add(new PointAttribute("intensity", PointAttributeTypes.DATA_TYPE_UINT16, 1));
    attributes.add(new PointAttribute("classification", PointAttributeTypes.DATA_TYPE_UINT8, 1));
    attributes.add(new PointAttribute("gps-time", PointAttributeTypes.DATA_TYPE_DOUBLE, 1));
    attributes.add(new PointAttribute("number of returns", PointAttributeTypes.DATA_TYPE_UINT8, 1));
    attributes.add(new PointAttribute("return number", PointAttributeTypes.DATA_TYPE_UINT8, 1));
    attributes.add(new PointAttribute("source id", PointAttributeTypes.DATA_TYPE_UINT16, 1));
    attributes.add(new PointAttribute("isSelected", PointAttributeTypes.DATA_TYPE_UINT8, 1));
    attributes.add(new PointAttribute("temperature", PointAttributeTypes.DATA_TYPE_FLOAT, 1));

    return attributes;
}

export class YDLasLoader {
    /**
     * 加载单个 LAS 文件
     * @param {string} url LAS 文件路径
     * @param {Function} callback 加载完成回调
     */
    static loadSingleLas(url, callback) {
        try {
            const xhr = XHRFactory.createXMLHttpRequest();
            xhr.open('GET', url, true);
            xhr.responseType = 'arraybuffer';
            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200 || xhr.status === 0) {
                        const buffer = xhr.response;
                        this.parseSingleLas(buffer, url, callback);
                    } else {
                        console.error(`Failed to load LAS file: ${url}, status: ${xhr.status}`);
                        callback(null);
                    }
                }
            };
            xhr.send(null);
        } catch (e) {
            console.error(`Error loading LAS file: ${e}`);
            callback(null);
        }
    }

    /**
     * 解析单个 LAS 文件缓冲区，构建点云几何结构
     * @param {ArrayBuffer} buffer 文件缓冲区
     * @param {string} url 文件路径
     * @param {Function} callback 解析完成回调
     */
    static async parseSingleLas(buffer, url, callback) {
        try {
            const lf = new LASFile(buffer);
            await lf.open();
            const header = await lf.getHeader();

            // 从 LAS 头信息提取边界框、缩放因子等
            const min = new THREE.Vector3(header.mins[0], header.mins[1], header.mins[2]);
            const max = new THREE.Vector3(header.maxs[0], header.maxs[1], header.maxs[2]);
            const boundingBox = new THREE.Box3(min, max);
            const tightBoundingBox = boundingBox.clone();
            const offset = new THREE.Vector3(...header.offset) || min.clone(); // 偏移量设为最小值
            const scale = new THREE.Vector3(
                header.scale[0] || 0.001,
                header.scale[1] || 0.001,
                header.scale[2] || 0.001
            );

            // 创建点云八叉树几何结构
            const pco = new PointCloudOctreeGeometry();
            pco.url = url;
            pco.octreeDir = url.substring(0, url.lastIndexOf('/')); // 目录路径
            pco.boundingBox = boundingBox;
            pco.tightBoundingBox = tightBoundingBox;
            pco.boundingSphere = boundingBox.getBoundingSphere(new THREE.Sphere());
            pco.offset = offset;
            pco.scale = scale;
            pco.spacing = header.spacing || 0.1; // 点间距（可根据实际数据调整）
            pco.pointAttributes = lasLazAttributes(); // 复用属性处理逻辑
            pco.loader = new LasLazLoader(new Version(lf.versionAsString), "las");

            // 创建根节点
            const root = new PointCloudOctreeGeometryNode(url.substring(url.lastIndexOf('/') + 1, url.length), pco, boundingBox);
            root.level = 0;
            root.numPoints = header.pointsCount;
            root.loaded = false;
            pco.root = root;

            // 触发根节点加载
            root.load();

            callback(pco);
        } catch (e) {
            console.error(`Error parsing LAS file: ${e}`);
            callback(null);
        }
    }
}